import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearchParams } from "react-router";
import {
  CircleCheck,
  Loader2,
  Phone,
  MessageCircle,
  Video,
  MapPin,
  TriangleAlert,
} from "lucide-react";
import {
  CONSULTATION_MODES,
  CONSULTATION_MODE_LABELS,
  SERVICE_INTERESTS,
  SERVICE_INTEREST_LABELS,
  TIME_SLOT_LABELS,
  createConsultationSchema,
  type ConsultationFormValues,
  type ConsultationMode,
  type CreateConsultationInput,
  type LeadSubmissionResponse,
} from "@veo/types";
import { ApiError, fetchApi } from "../lib/api";
import {
  FormField,
  HoneypotField,
  RadioCards,
  SelectInput,
  TextArea,
  TextInput,
} from "../components/FormControls";
import { Reveal } from "../components/motion";
import { SectionList } from "../blocks/registry";
import { loadOptionalPage, pageMeta } from "../lib/page";
import { seoMeta } from "../lib/seo";
import { loadSectionData } from "../lib/section-data";
import type { Route } from "./+types/book-consultation";

export async function loader({ request }: Route.LoaderArgs) {
  const preview = new URL(request.url).searchParams.get("preview");
  const page = await loadOptionalPage("/book-consultation", preview);

  return { page, sectionData: page ? await loadSectionData(page.sections) : {} };
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  if (loaderData?.page) return pageMeta(loaderData.page, { matches });

  return seoMeta({
    matches,
    title: "Book a consultation — VEO Consultancy",
    description:
      "Book a first conversation with a VEO Consultancy adviser. One hour, no cost, no obligation.",
    path: "/book-consultation",
  });
}

const MODE_ICONS: Record<ConsultationMode, typeof Phone> = {
  PHONE_CALL: Phone,
  WHATSAPP: MessageCircle,
  VIDEO_CALL: Video,
  IN_PERSON: MapPin,
};

export default function BookConsultation({ loaderData }: Route.ComponentProps) {
  const { page, sectionData } = loaderData;
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState<{ referenceCode?: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  /**
   * Recorded once on mount and submitted with the form.
   *
   * The server rejects anything completed in under three seconds. A person
   * reading and filling in this form cannot manage that; a script can.
   */
  /**
   * Captured once, on first render, via a lazy state initialiser.
   *
   * `useRef(Date.now())` would evaluate the timestamp on every render — the
   * value is discarded after the first, but calling it during render is
   * impure and the React Compiler rightly refuses to optimise around it.
   */
  const [formLoadedAt] = useState(() => Date.now());

  const preselected = searchParams.get("service");

  const {
    register,
    handleSubmit,
    watch,
    setValue,

    formState: { errors, isSubmitting },
  } = useForm<ConsultationFormValues, unknown, CreateConsultationInput>({
    resolver: zodResolver(createConsultationSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      whatsappNumber: "",
      serviceInterest: (SERVICE_INTERESTS.includes(preselected as never)
        ? preselected
        : "GENERAL") as never,
      preferredChannel: "PHONE_CALL",
      preferredDate: "",
      preferredTimeSlot: "",
      message: "",
      website: "",
    },
  });

  const preferredChannel = watch("preferredChannel");

  /** Move focus to the confirmation so it is announced, not just displayed. */
  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const result = await fetchApi<LeadSubmissionResponse>("/public/consultations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, formLoadedAt }),
      });
      setSubmitted({ referenceCode: result.referenceCode });
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setFormError(
          "You have submitted several requests recently. Please wait a little while, or call us directly on +254 700 000 000.",
        );
      } else if (error instanceof ApiError && error.status === 422) {
        setFormError("Please check the highlighted fields and try again.");
      } else {
        setFormError(
          "We could not submit your request just now. Please try again, or email info@veoconsultancy.com.",
        );
      }
    }
  });

  // Minimum selectable date is tomorrow: a same-day request cannot be honoured.
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

  if (submitted) {
    return (
      <main id="main">
        <section className="bg-surface-100 textured">
          <div className="container-site section-y">
            <div
              ref={successRef}
              tabIndex={-1}
              role="status"
              className="container-content rounded-xl bg-white p-10 text-center shadow-card focus:outline-none"
            >
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-growth-50">
                <CircleCheck aria-hidden className="size-7 text-growth-600" />
              </span>

              <h1 className="mt-6 text-4xl">Request received</h1>
              <p className="mt-4 text-lg leading-relaxed text-ink-600">
                An adviser will be in touch within one business day to confirm a time. We have
                emailed you a copy for your records.
              </p>

              {submitted.referenceCode && (
                <div className="mt-8 inline-block rounded-lg border border-ink-200 bg-ink-50 px-6 py-4">
                  <p className="text-xs uppercase tracking-wider text-ink-500">Your reference</p>
                  <p className="mt-1 font-mono text-lg font-semibold text-navy-900">
                    {submitted.referenceCode}
                  </p>
                </div>
              )}

              <p className="mt-8 text-sm text-ink-500">
                Nothing further is needed from you. If you would like to get in touch before then,
                quote your reference.
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to="/insights"
                  className="rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-cream-50 hover:bg-navy-800"
                >
                  Read our insights
                </Link>
                <Link
                  to="/"
                  className="rounded-md px-6 py-3 text-sm font-semibold text-navy-900 ring-1 ring-inset ring-ink-300 hover:bg-ink-50"
                >
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // After the submitted branch on purpose: someone who has just booked should
  // see their confirmation, whichever layout the page is using.
  if (page) {
    return (
      <main id="main">
        <SectionList sections={page.sections} data={sectionData} />
      </main>
    );
  }

  return (
    <main id="main">
      <section className="bg-navy-900 textured-dark text-cream-100">
        <div className="container-site py-16">
          <div className="container-content">
            <p className="eyebrow text-gold-300">No cost, no obligation</p>
            <h1 className="mt-4 text-5xl text-cream-50">Book a consultation</h1>
            <p className="mt-5 text-lg leading-relaxed text-navy-100">
              An hour with an adviser to talk through your circumstances. Sometimes the answer is
              that you do not need us yet — that is a legitimate outcome, and we will say so.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface-100 textured">
        <div className="container-site section-y">
          <Reveal className="container-content">
            <form
              onSubmit={onSubmit}
              noValidate
              className="relative space-y-7 rounded-xl bg-white p-7 shadow-card md:p-10"
            >
              <HoneypotField register={register("website")} />

              {formError && (
                <div
                  role="alert"
                  className="flex gap-3 rounded-md bg-danger-50 p-4 ring-1 ring-inset ring-danger-500/20"
                >
                  <TriangleAlert aria-hidden className="size-5 shrink-0 text-danger-500" />
                  <p className="text-sm text-danger-700">{formError}</p>
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  label="Full name"
                  htmlFor="fullName"
                  required
                  error={errors.fullName?.message}
                  className="sm:col-span-2"
                >
                  <TextInput
                    id="fullName"
                    autoComplete="name"
                    aria-invalid={Boolean(errors.fullName)}
                    aria-describedby={errors.fullName ? "fullName-error" : undefined}
                    {...register("fullName")}
                  />
                </FormField>

                <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
                  <TextInput
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    {...register("email")}
                  />
                </FormField>

                <FormField
                  label="Phone"
                  htmlFor="phone"
                  required
                  error={errors.phone?.message}
                  hint="For example 0712 345 678 or +254 712 345 678"
                >
                  <TextInput
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? "phone-error" : "phone-hint"}
                    {...register("phone")}
                  />
                </FormField>

                <FormField
                  label="WhatsApp number"
                  htmlFor="whatsappNumber"
                  error={errors.whatsappNumber?.message}
                  hint="Only if it differs from the number above"
                >
                  <TextInput
                    id="whatsappNumber"
                    type="tel"
                    aria-invalid={Boolean(errors.whatsappNumber)}
                    {...register("whatsappNumber")}
                  />
                </FormField>

                <FormField
                  label="Service of interest"
                  htmlFor="serviceInterest"
                  required
                  error={errors.serviceInterest?.message}
                >
                  <SelectInput id="serviceInterest" {...register("serviceInterest")}>
                    {SERVICE_INTERESTS.map((interest) => (
                      <option key={interest} value={interest}>
                        {SERVICE_INTEREST_LABELS[interest]}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              </div>

              <RadioCards
                name="preferredChannel"
                legend="How would you like to meet?"
                columns={4}
                value={preferredChannel as ConsultationMode}
                onChange={(value) => setValue("preferredChannel", value)}
                error={errors.preferredChannel?.message}
                options={CONSULTATION_MODES.map((mode) => {
                  const ModeIcon = MODE_ICONS[mode];
                  return {
                    value: mode,
                    label: CONSULTATION_MODE_LABELS[mode],
                    icon: <ModeIcon className="size-4" />,
                  };
                })}
              />

              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  label="Preferred date"
                  htmlFor="preferredDate"
                  error={errors.preferredDate?.message}
                >
                  <TextInput
                    id="preferredDate"
                    type="date"
                    min={tomorrow}
                    aria-invalid={Boolean(errors.preferredDate)}
                    {...register("preferredDate")}
                  />
                </FormField>

                <FormField
                  label="Preferred time"
                  htmlFor="preferredTimeSlot"
                  error={errors.preferredTimeSlot?.message}
                >
                  <SelectInput id="preferredTimeSlot" {...register("preferredTimeSlot")}>
                    <option value="">No preference</option>
                    {Object.entries(TIME_SLOT_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              </div>

              <FormField
                label="Anything you would like us to know"
                htmlFor="message"
                error={errors.message?.message}
                hint="A sentence or two is plenty. We will ask the rest when we speak."
              >
                <TextArea
                  id="message"
                  aria-invalid={Boolean(errors.message)}
                  {...register("message")}
                />
              </FormField>

              {/* Consent — unticked, explicit, and required by the schema. */}
              <div className="rounded-md bg-ink-50 p-5">
                <label className="flex gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 size-4 shrink-0 rounded border-ink-400 text-navy-900 focus:ring-2 focus:ring-navy-700"
                    aria-invalid={Boolean(errors.consent)}
                    aria-describedby={errors.consent ? "consent-error" : undefined}
                    {...register("consent")}
                  />
                  <span className="text-sm leading-relaxed text-ink-700">
                    I consent to VEO Consultancy storing and using these details in order to respond
                    to my enquiry. I understand I can ask for them to be corrected or erased at any
                    time, as set out in the{" "}
                    <Link to="/privacy-policy" className="font-medium text-navy-700 underline">
                      Privacy Policy
                    </Link>
                    .
                    <span className="ml-1 text-danger-500" aria-hidden>
                      *
                    </span>
                  </span>
                </label>
                {errors.consent && (
                  <p
                    id="consent-error"
                    role="alert"
                    className="mt-2 text-xs font-medium text-danger-500"
                  >
                    {errors.consent.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-4 text-base font-semibold text-cream-50 transition-colors hover:bg-navy-800 disabled:cursor-not-allowed disabled:bg-navy-900/50"
              >
                {isSubmitting && <Loader2 aria-hidden className="size-4 animate-spin" />}
                {isSubmitting ? "Sending your request…" : "Request a consultation"}
              </button>

              <p className="text-center text-xs leading-relaxed text-ink-500">
                We reply within one business day. Your details are never sold or shared with third
                parties.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
